import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react'; // Keep ChevronDown if used elsewhere in the component for navigation
import '../../css/platformCalendar.css';

const PlatformCalendar = ({
  onChange,
  value,
  selectRange = false,
  minDate,
  maxDate,
  className = '',
  // 'day' (default) is the normal day-grid picker. 'month' locks the
  // calendar to the year/months view so the user picks whole months
  // (optionally a start/end month range) instead of drilling into days.
  pickerMode = 'day',
  // When false, the header title can't be clicked into the year/decade drill-down —
  // keeps single-date pickers (e.g. candidate forms) on the plain day grid.
  allowDrillDown = true
}) => {
  // const isMonthPicker = pickerMode === 'month';
  // const [view, setView] = useState(isMonthPicker ? 'year' : 'month');
  const isMonthPicker = pickerMode === 'month';
  const [view, setView] = useState(isMonthPicker ? 'year' : 'month');



  const [currentDate, setCurrentDate] = useState(() => {
    if (Array.isArray(value) && value[0]) return new Date(value[0]);
    if (value && !Array.isArray(value)) return new Date(value);
    return new Date();
  });
  
  const [hoverDate, setHoverDate] = useState(null);

  const isRange = selectRange;
  let startDate = null;
  let endDate = null;
  
  if (isRange && Array.isArray(value)) {
    startDate = value[0] ? new Date(value[0]) : null;
    endDate = value[1] ? new Date(value[1]) : null;
  } else if (!isRange && value) {
    startDate = new Date(value);
  }

  // Normalize minDate & maxDate
  const min = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate()) : null;
  const max = maxDate ? new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate()) : null;

  const today = new Date();
  const isSameDay = (d1, d2) => d1 && d2 && d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  const isSameMonth = (d1, d2) => d1 && d2 && d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
  const isBeforeDay = (d1, d2) => d1 && d2 && new Date(d1.getFullYear(), d1.getMonth(), d1.getDate()) < new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
  const isAfterDay = (d1, d2) => d1 && d2 && new Date(d1.getFullYear(), d1.getMonth(), d1.getDate()) > new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());

  const isBeforeMonth = (d1, d2) => d1 && d2 && (d1.getFullYear() * 12 + d1.getMonth()) < (d2.getFullYear() * 12 + d2.getMonth());
  const isAfterMonth = (d1, d2) => d1 && d2 && (d1.getFullYear() * 12 + d1.getMonth()) > (d2.getFullYear() * 12 + d2.getMonth());

  const handleMonthSelect = (monthIndex) => {
    const clicked = new Date(currentDate.getFullYear(), monthIndex, 1);
    if (min && clicked.getFullYear() === min.getFullYear() && monthIndex < min.getMonth()) return;
    if (max && clicked.getFullYear() === max.getFullYear() && monthIndex > max.getMonth()) return;

    if (isRange) {
      if (!startDate || (startDate && endDate)) {
        onChange([clicked, null]);
      } else if (isBeforeMonth(clicked, startDate)) {
        onChange([clicked, null]);
      } else {
        const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        const end = new Date(clicked.getFullYear(), clicked.getMonth() + 1, 0, 23, 59, 59, 999);
        onChange([start, end]);
      }
    } else {
      onChange(clicked);
    }
  };

  // Whether stepping back/forward a period would land somewhere with nothing selectable —
  // used to both block the navigation and grey out the arrow.
  const prevDisabled = (() => {
    if (view === 'month') {
      const prevMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      return !!(min && isBeforeDay(prevMonthEnd, min));
    }
    if (view === 'year') return !!(min && currentDate.getFullYear() - 1 < min.getFullYear());
    if (view === 'decade') {
      const decadeStart = Math.floor(currentDate.getFullYear() / 10) * 10;
      return !!(min && decadeStart - 1 < min.getFullYear());
    }
    return false;
  })();

  const nextDisabled = (() => {
    if (view === 'month') {
      const nextMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
      return !!(max && isAfterDay(nextMonthStart, max));
    }
    if (view === 'year') return !!(max && currentDate.getFullYear() + 1 > max.getFullYear());
    if (view === 'decade') {
      const decadeStart = Math.floor(currentDate.getFullYear() / 10) * 10;
      return !!(max && decadeStart + 10 > max.getFullYear());
    }
    return false;
  })();

  const handlePrev = () => {
    if (prevDisabled) return;
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (view === 'year') {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1));
    } else if (view === 'decade') {
      const decadeStart = Math.floor(currentDate.getFullYear() / 10) * 10;
      setCurrentDate(new Date(decadeStart - 10, currentDate.getMonth(), 1));
    }
  };

  const handleNext = () => {
    if (nextDisabled) return;
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (view === 'year') {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1));
    } else if (view === 'decade') {
      const decadeStart = Math.floor(currentDate.getFullYear() / 10) * 10;
      setCurrentDate(new Date(decadeStart + 10, currentDate.getMonth(), 1));
    }
  };

  const handleTitleClick = () => {
    if (!allowDrillDown) return;
    if (view === 'month') {
      setView('year');
    } else if (view === 'year') {
      setView('decade');
    } else {
      setView('year');
    }
  };

  const handleYearClick = (year) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
    setView('year');
  };

  const handleDayClick = (date) => {
    if (min && isBeforeDay(date, min)) return;
    if (max && isAfterDay(date, max)) return;

    if (isRange) {
      if (!startDate || (startDate && endDate)) {
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);

        onChange([start, null]);
      } else if (startDate && !endDate) {
        if (isBeforeDay(date, startDate)) {
          const start = new Date(date);
          start.setHours(0, 0, 0, 0);

          onChange([start, null]);
        } else {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);

          const end = new Date(date);
          end.setHours(23, 59, 59, 999); // ✅ covers both same & different day

          onChange([start, end]);
        }
      }
    } else {
      onChange(date);
    }
  };

  const handleMonthClick = (monthIndex) => {
    const newDate = new Date(currentDate.getFullYear(), monthIndex, 1);
    if (min && newDate.getFullYear() === min.getFullYear() && monthIndex < min.getMonth()) return; 
    if (max && newDate.getFullYear() === max.getFullYear() && monthIndex > max.getMonth()) return; 
    setCurrentDate(newDate);
    setView('month');
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];
    
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, daysInPrevMonth - i);
      days.push({ date: d, isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, isCurrentMonth: true });
    }
    
    const totalCells = 42;
    const nextDays = totalCells - days.length;
    for (let i = 1; i <= nextDays; i++) {
      const d = new Date(year, month + 1, i);
      days.push({ date: d, isCurrentMonth: false });
    }

    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

    return (
      <div className="react-calendar__viewContainer react-calendar__month-view">
        <div className="react-calendar__month-view__weekdays">
          {weekdays.map(day => (
            <div key={day} className="react-calendar__month-view__weekdays__weekday">
              <abbr title={day}>{day}</abbr>
            </div>
          ))}
        </div>
        <div className="react-calendar__month-view__days">
          {days.map((dayObj, index) => {
            const { date, isCurrentMonth } = dayObj;
            
            const isToday = isSameDay(date, today);
            // const isStart = isSameDay(date, startDate);
            // const isEnd = isSameDay(date, endDate);

            const isStart = startDate && 
              date.getDate() === startDate.getDate() && 
              date.getMonth() === startDate.getMonth() && 
              date.getFullYear() === startDate.getFullYear();

            const isEnd = endDate && 
              date.getDate() === endDate.getDate() && 
              date.getMonth() === endDate.getMonth() && 
              date.getFullYear() === endDate.getFullYear();
            
            let isBetween = false;
            if (isRange && startDate && endDate) {
              isBetween = isAfterDay(date, startDate) && isBeforeDay(date, endDate);
            } else if (isRange && startDate && hoverDate && !endDate) {
              if (isAfterDay(hoverDate, startDate)) {
                isBetween = isAfterDay(date, startDate) && isBeforeDay(date, hoverDate);
              }
            }

            const active = isStart || isEnd;
            const disabled = (min && isBeforeDay(date, min)) || (max && isAfterDay(date, max));

            let classes = ['react-calendar__tile', 'react-calendar__month-view__days__day'];
            if (!isCurrentMonth) classes.push('react-calendar__tile--neighboringMonth');
            if (isToday) classes.push('react-calendar__tile--now');
            if (active) classes.push('react-calendar__tile--active');
            if (isStart) classes.push('react-calendar__tile--rangeStart');
            if (isEnd) classes.push('react-calendar__tile--rangeEnd');
            if (active || isBetween) classes.push('react-calendar__tile--range');
            if (isBetween) classes.push('react-calendar__tile--rangeBetween');
            
            return (
              <button
                key={index}
                disabled={disabled}
                className={classes.join(' ')}
                onClick={() => handleDayClick(date)}
                onMouseEnter={() => setHoverDate(date)}
                onMouseLeave={() => setHoverDate(null)}
                style={{ color: (isStart || isEnd || isBetween) ? '#FFFFFF' : '' }}
              >
                <abbr 
                  aria-label={date.toDateString()}
                  style={{
                    color: (isStart || isEnd || isBetween) ? '#000' : undefined,
                    position: 'relative',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}
  >
                  {date.getDate()}
                </abbr>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearView = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    return (
      <div className="react-calendar__viewContainer react-calendar__year-view">
        <div className="react-calendar__year-view__months">
          {months.map((monthName, index) => {
            const date = new Date(currentDate.getFullYear(), index, 1);
            const isCurrentMonth = isSameMonth(date, today);
            
            let active = false;
            if (isRange) {
               active = (startDate && isSameMonth(date, startDate)) || (endDate && isSameMonth(date, endDate));
            } else {
               active = startDate && isSameMonth(date, startDate);
            }


            let isBetween = false;
            if (isMonthPicker && isRange) {
              if (startDate && endDate) {
                isBetween = isAfterMonth(date, startDate) && isBeforeMonth(date, endDate);
              } else if (startDate && hoverDate && !endDate) {
                if (isAfterMonth(hoverDate, startDate)) {
                  isBetween = isAfterMonth(date, startDate) && isBeforeMonth(date, hoverDate);
                }
              }
            }


            const disabled =
              (min && (currentDate.getFullYear() < min.getFullYear() ||
                (currentDate.getFullYear() === min.getFullYear() && index < min.getMonth()))) ||
              (max && (currentDate.getFullYear() > max.getFullYear() ||
                (currentDate.getFullYear() === max.getFullYear() && index > max.getMonth())));

            let classes = ['react-calendar__tile', 'react-calendar__year-view__months__month'];
            if (isCurrentMonth) classes.push('react-calendar__tile--now');
            if (active || isBetween) classes.push('react-calendar__tile--active', 'react-calendar__tile--range');

            return (
              <button
                key={monthName}
                disabled={disabled}
                className={classes.join(' ')}
                onClick={() => (isMonthPicker ? handleMonthSelect(index) : handleMonthClick(index))}
                onMouseEnter={() => isMonthPicker && setHoverDate(date)}
                onMouseLeave={() => isMonthPicker && setHoverDate(null)}
              >
                <abbr 
                  aria-label={monthName}
                  style={{ color: active ? '#FFFFFF' : 'inherit' }}
                >
                  {monthName}
                </abbr>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDecadeView = () => {
    const decadeStart = Math.floor(currentDate.getFullYear() / 10) * 10;
    const years = [];
    for (let i = 0; i < 12; i++) {
      const year = decadeStart + i;
      years.push(year);
    }
    
    return (
      <div className="react-calendar__viewContainer react-calendar__decade-view">
        <div className="react-calendar__year-view__months">
          {years.map((year) => {
            const isCurrentYear = today.getFullYear() === year;
            
            let active = false;
            if (isRange) {
              active = (startDate && startDate.getFullYear() === year) || (endDate && endDate.getFullYear() === year);
            } else {
              active = startDate && startDate.getFullYear() === year;
            }

            const disabled = (min && year < min.getFullYear()) || (max && year > max.getFullYear());

            let classes = ['react-calendar__tile', 'react-calendar__year-view__months__year'];
            if (isCurrentYear) classes.push('react-calendar__tile--now');
            if (active) classes.push('react-calendar__tile--active');

            return (
              <button
                key={year}
                disabled={disabled}
                className={classes.join(' ')}
                onClick={() => handleYearClick(year)}
              >
                <abbr 
                  aria-label={year.toString()}
                  style={{ color: active ? '#FFFFFF' : 'inherit' }}
                >
                  {year}
                </abbr>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const titleText = view === 'month' 
      ? `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`
      : view === 'year' 
        ? `${currentDate.getFullYear()}`
        : `${Math.floor(currentDate.getFullYear() / 10) * 10} - ${Math.floor(currentDate.getFullYear() / 10) * 10 + 9}`;

  return (
    <div className={`platform-calendar react-calendar ${className}`}>
      <style>{`
        .platform-calendar .react-calendar__month-view__days .react-calendar__tile--now,
        .platform-calendar .react-calendar__month-view__days .react-calendar__tile--now:enabled:hover,
        .platform-calendar .react-calendar__month-view__days .react-calendar__tile--now abbr,
        .platform-calendar .react-calendar__month-view__days .react-calendar__tile--now:enabled:hover abbr {
            color: #FFFFFF !important;
            -webkit-text-fill-color: #FFFFFF !important;
        }

        /* Kill the hovering capsule specifically on the current date */
        .platform-calendar .react-calendar__month-view__days .react-calendar__tile--now:enabled:hover {
            background-color: transparent !important;
            background: transparent !important;
            border-radius: 50% !important;
        }
        /* Keep the distinct inner circle for today intact on hover */
        .platform-calendar .react-calendar__month-view__days .react-calendar__tile--now:enabled:hover::before {
            content: '';
            position: absolute;
            width: 42px !important;
            height: 42px !important;
            background: #6B7280 !important;
            border-radius: 50% !important;
            z-index: -1;
        }
      `}</style>
      <div className="react-calendar__navigation">
        <button className="react-calendar__navigation__arrow" onClick={handlePrev} disabled={prevDisabled}>
           <span style={{ fontSize: "40px", fontWeight: 300, lineHeight: "1" }}>‹</span>
        </button>

        <button className="react-calendar__navigation__label" onClick={handleTitleClick} disabled={!allowDrillDown}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>{titleText}</span>
            {view === 'month' && allowDrillDown && <ChevronDown size={24} />}
          </div>
        </button>

        <button className="react-calendar__navigation__arrow" onClick={handleNext} disabled={nextDisabled}>
           <span style={{ fontSize: "40px", fontWeight: 300, lineHeight: "1" }}>›</span>
        </button>
      </div>
      <div className="react-calendar__view-wrapper">        {view === 'month' ? renderMonthView() : view === 'year' ? renderYearView() : renderDecadeView()}
      </div>
    </div>
  );
};

export default PlatformCalendar;
